import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { AppService } from './app.service';
import { HttpErrorService } from './http-error.service';
import {
  CustomerDataResponseModel,
  CustomerListResponseModel,
  CustomerModuleResponseModel,
  ImportSourceResponseModel,
  RepositoryModuleResponseModel,
  ServerSideResponse
} from '../model/types';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private readonly http = inject(HttpClient);
  private readonly appService = inject(AppService);
  private readonly authService = inject(AuthService);
  private readonly errorService = inject(HttpErrorService);

  // --- Modules contrat (GET_CUSTOMER_MODULES, client = 1er ligne CustomerList) ---

  private readonly contractModules = signal<CustomerModuleResponseModel[]>([]);
  readonly contractModulesLoaded = signal(false);
  private contractModulesLoad$: Observable<void> | null = null;

  /** MODULE_CODE actifs (`IS_MODULE_ACTIVE === '1'`) pour garde et templates. */
  readonly activeContractModuleCodes = computed(() =>
    new Set(
      this.contractModules()
        .filter(m => m.IS_MODULE_ACTIVE === '1')
        .map(m => m.MODULE_CODE)
    )
  );

  /**
   * Charge une fois les modules du client courant (même source que la fiche client : 1er customer de la liste).
   * Idempotent ; réutilisé par le guard et le préchargement après login.
   */
  ensureContractModulesLoaded(): Observable<void> {
    if (this.contractModulesLoaded()) {
      return of(undefined);
    }
    if (!this.contractModulesLoad$) {
      this.contractModulesLoad$ = this.fetchContractModulesForCurrentCustomer().pipe(shareReplay(1));
    }
    return this.contractModulesLoad$;
  }

  /** À appeler à la déconnexion pour ne pas réutiliser l'état du client précédent. */
  clearContractModuleState(): void {
    this.contractModules.set([]);
    this.contractModulesLoaded.set(false);
    this.contractModulesLoad$ = null;
  }

  /**
   * Vérité métier une fois les modules connus (guards après `ensureContractModulesLoaded`, etc.).
   * Tant que `contractModulesLoaded` est false, `activeContractModuleCodes` est vide : ne pas utiliser
   * seul pour l'UI — préférer `isContractModuleNavEnabled`.
   */
  isContractModuleActive(moduleCode: string): boolean {
    return this.activeContractModuleCodes().has(moduleCode);
  }

  /**
   * Affichage menu / cartes : tant que le chargement n'est pas fini, on ne grise pas (évite un Set vide
   * confondu avec « inactif »). Après chargement : aligné sur `isContractModuleActive`.
   */
  isContractModuleNavEnabled(moduleCode: string): boolean {
    if (!this.contractModulesLoaded()) {
      return true;
    }
    return this.isContractModuleActive(moduleCode);
  }

  private fetchContractModulesForCurrentCustomer(): Observable<void> {
    return this.getCustomerList().pipe(
      map(response => this.getCustomerListRows(response)),
      map(rows => rows[0]?.ID_REPOSITORY_CUSTOMER),
      switchMap(customerId => {
        if (!customerId) {
          return of([] as CustomerModuleResponseModel[]);
        }
        return this.getCustomerModules(customerId).pipe(
          map(r => this.getCustomerModuleRows(r))
        );
      }),
      tap(list => {
        if (this.authService.isLoggedIn() !== true) {
          return;
        }
        this.contractModules.set(list);
        this.contractModulesLoaded.set(true);
      }),
      map(() => undefined)
    );
  }

  /**
   * GET_CUSTOMER_LIST
   */
  getCustomerList(): Observable<ServerSideResponse> {
    return this.http.post<ServerSideResponse>(`${environment.apiUrl}/api/CustomerList/Get`, {
      filter: {
        LanguageCode: this.appService.languageCode(),
      },
    }).pipe(
      catchError(err => of({
        draw: 0,
        recordsTotal: 0,
        recordsFiltered: 0,
        data: [],
        ColSums: {},
        dataType: '',
        error: this.errorService.formatError(err)
      }))
    );
  }

  /**
   * GET_CUSTOMER_DATA
   */
  getCustomerData(idRepositoryCustomer: number): Observable<ServerSideResponse> {
    return this.http.post<ServerSideResponse>(`${environment.apiUrl}/api/CustomerData/Get`, {
      filter: {
        IdRepositoryCustomer: idRepositoryCustomer,
        LanguageCode: this.appService.languageCode(),
      },
    }).pipe(
      catchError(err => of({
        draw: 0,
        recordsTotal: 0,
        recordsFiltered: 0,
        data: [],
        ColSums: {},
        dataType: '',
        error: this.errorService.formatError(err)
      }))
    );
  }

  /**
   * GET_REPOSITORY_MODULE_LIST — MODULE_DESCRIPTION selon filter.LanguageCode (comme report : locale app).
   */
  getRepositoryModuleList(): Observable<ServerSideResponse> {
    return this.http.post<ServerSideResponse>(`${environment.apiUrl}/api/RepositoryModuleList/Get`, {
      filter: {
        LanguageCode: this.appService.languageCode(),
      },
    }).pipe(
      catchError(err => of({
        draw: 0,
        recordsTotal: 0,
        recordsFiltered: 0,
        data: [],
        ColSums: {},
        dataType: '',
        error: this.errorService.formatError(err)
      }))
    );
  }

  /**
   * GET_CUSTOMER_MODULES — MODULE_DESCRIPTION selon filter.LanguageCode (comme report : locale app).
   */
  getCustomerModules(idRepositoryCustomer: number): Observable<ServerSideResponse> {
    return this.http.post<ServerSideResponse>(`${environment.apiUrl}/api/CustomerModules/Get`, {
      filter: {
        IdRepositoryCustomer: idRepositoryCustomer,
        LanguageCode: this.appService.languageCode(),
      },
    }).pipe(
      catchError(err => of({
        draw: 0,
        recordsTotal: 0,
        recordsFiltered: 0,
        data: [],
        ColSums: {},
        dataType: '',
        error: this.errorService.formatError(err)
      }))
    );
  }

  /**
   * CREATE_UPDATE_CUSTOMER_MODULE
   */
  updateCustomerModuleStatus(
    idRepositoryCustomer: number,
    idRepositoryModule: number,
    isModuleActive: '0' | '1',
    updatedBy?: string
  ): Observable<number> {
    const loggedUserName = this.authService.loggedUser()?.UserName;

    return this.http.post<number>(`${environment.apiUrl}/api/CreateUpdateCustomerModule/Invoke`, {
      filter: {
        IdRepositoryCustomer: idRepositoryCustomer,
        IdRepositoryModule: idRepositoryModule,
        IsModuleActive: isModuleActive,
        UpdatedBy: updatedBy ?? loggedUserName ?? '',
      },
    }).pipe(
      catchError(err => {
        this.errorService.formatError(err);
        return of(-1);
      })
    );
  }

  getCustomerListRows(response: ServerSideResponse): CustomerListResponseModel[] {
    return (response?.data ?? []) as CustomerListResponseModel[];
  }

  getCustomerDataRow(response: ServerSideResponse): CustomerDataResponseModel | undefined {
    const [row] = (response?.data ?? []) as CustomerDataResponseModel[];
    return row;
  }

  /**
   * Loads contract data for the current customer (first row of GET_CUSTOMER_LIST).
   */
  getCurrentCustomerData(): Observable<CustomerDataResponseModel | null> {
    return this.getCustomerList().pipe(
      map(response => this.getCustomerListRows(response)),
      map(customers => customers[0]?.ID_REPOSITORY_CUSTOMER),
      switchMap(customerId => {
        if (!customerId) {
          return of(null);
        }

        return this.getCustomerData(customerId).pipe(
          map(response => this.getCustomerDataRow(response) ?? null)
        );
      })
    );
  }

  /**
   * Returns true when active user licenses have reached the contractual maximum.
   */
  isLicenseLimitReached(customerData: CustomerDataResponseModel | null | undefined): boolean {
    if (!customerData) {
      return false;
    }

    const used = customerData.LICENSE_COUNT_USED;
    const max = customerData.LICENSE_COUNT_MAX;

    if (used == null || max == null) {
      return false;
    }

    return used >= max;
  }

  getCustomerModuleRows(response: ServerSideResponse): CustomerModuleResponseModel[] {
    return (response?.data ?? []) as CustomerModuleResponseModel[];
  }

  getRepositoryModuleRows(response: ServerSideResponse): RepositoryModuleResponseModel[] {
    return (response?.data ?? []) as RepositoryModuleResponseModel[];
  }

  /**
   * GET_IMPORT_SOURCE_LIST
   */
  getImportSourceList(idRepositoryCustomer: number): Observable<ServerSideResponse> {
    return this.http.post<ServerSideResponse>(`${environment.apiUrl}/api/ImportSourceList/Get`, {
      filter: {
        IdRepositoryCustomer: idRepositoryCustomer,
        LanguageCode: this.appService.languageCode(),
      },
    }).pipe(
      catchError(err => of({
        draw: 0,
        recordsTotal: 0,
        recordsFiltered: 0,
        data: [],
        ColSums: {},
        dataType: '',
        error: this.errorService.formatError(err)
      }))
    );
  }

  /**
   * CREATE_UPDATE_IMPORT_SOURCE
   */
  createUpdateImportSource(
    idRepositoryCustomer: number,
    toolName: string,
    companyName: string,
    importType: string,
    importFrequency: string,
    idRepositoryImportSource?: number | null,
    toolType?: string | null,
    dataScope?: string | null
  ): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/api/CreateUpdateImportSource/Invoke`, {
      filter: {
        IdRepositoryCustomer: idRepositoryCustomer,
        ToolName: toolName,
        CompanyName: companyName,
        ImportType: importType,
        ImportFrequency: importFrequency,
        IdRepositoryImportSource: idRepositoryImportSource ?? null,
        ToolType: toolType ?? null,
        DataScope: dataScope ?? null,
      },
    }).pipe(
      catchError(err => {
        this.errorService.formatError(err);
        return of(-1);
      })
    );
  }

  /**
   * DELETE_IMPORT_SOURCE
   */
  deleteImportSource(idRepositoryImportSource: number): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/api/DeleteImportSource/Invoke`, {
      filter: {
        IdRepositoryImportSource: idRepositoryImportSource,
      },
    }).pipe(
      catchError(err => {
        this.errorService.formatError(err);
        return of(-1);
      })
    );
  }

  getImportSourceRows(response: ServerSideResponse): ImportSourceResponseModel[] {
    return (response?.data ?? []) as ImportSourceResponseModel[];
  }

  sendContractRequest(_message: string): Observable<number> {
    return throwError(() => new Error('Endpoint not yet available'));
  }
}
